import type { ImageProps, Slide } from '@features/powerpoint';
import { chartOptionsToBase64Svg } from '@features/powerpoint';
import { contentSlideHeaderV2 } from '../../../../../../utils/slide-header-v2';
import {
  CHART_WIDTH,
  getChartHeight,
} from '../../../campaign-analytics/sharedLayout';
import {
  type AudienceSegment,
  DIMENSIONS_PER_GRAPH,
  PREDICTIVE_GROUP_VALUE,
} from '@components/shared/CampaignAnalytics/ChartDefinitions/AudienceProfile';
import { annotationsTextContent } from '../../../../../../slides/shared/annotations-text';
import { buildVisitsChart } from '@configurations/campaignAnalytics/audienceProfileChart';
import { sortBy } from '@utils/arrays';
import { truncateAudienceGroupNames } from '@features/campaign-analytics';
import { CHART_IMAGE_OPTIONS } from '../plain-chart-slides/charts-v2';
import type { CampaignAnalyticsSlidesV2Props } from '../build-campaign-analytics-slides-v2';

const MAX_DIMENSIONS = 30;

export const filterByPredictiveGroup = (data: AudienceSegment[]) => {
  return data.some(item => item.group === PREDICTIVE_GROUP_VALUE)
    ? data.filter(item => item.group === PREDICTIVE_GROUP_VALUE)
    : data;
};

export const audienceProfileChartSlideV2 = (
  props: CampaignAnalyticsSlidesV2Props
): Slide[] => {
  const chartHeight = getChartHeight(
    props.campaignAnalyticsReport.audienceProfile
  );

  const visitedData =
    props.campaignAnalyticsReport.audienceProfile.data.visited;

  const defaultVisitedData = visitedData.some(
    item => item.group === PREDICTIVE_GROUP_VALUE
  )
    ? visitedData.filter(item => item.group === PREDICTIVE_GROUP_VALUE)
    : visitedData;

  const slideCount = Math.min(
    Math.ceil(defaultVisitedData.length / DIMENSIONS_PER_GRAPH),
    Math.ceil(MAX_DIMENSIONS / DIMENSIONS_PER_GRAPH)
  );

  return Array.from({ length: slideCount }, (_, index) => {
    const startIndex = index * DIMENSIONS_PER_GRAPH;
    const endIndex = Math.min(
      startIndex + DIMENSIONS_PER_GRAPH,
      MAX_DIMENSIONS
    );

    const sortedVisitedData = truncateAudienceGroupNames(
      defaultVisitedData
        .toSorted(sortBy('value', 'desc'))
        .slice(startIndex, endIndex)
    );

    const visitsChartOptions = buildVisitsChart(
      sortedVisitedData,
      'Saw the Ad and Visited',
      true
    );

    const visitsChartSVG = chartOptionsToBase64Svg({
      chartOptions: visitsChartOptions,
      width: CHART_WIDTH,
      height: chartHeight,
    });

    const images: ImageProps[] = [
      {
        data: visitsChartSVG,
        w: CHART_IMAGE_OPTIONS.w,
        h: CHART_IMAGE_OPTIONS.h,
        x: CHART_IMAGE_OPTIONS.x,
        y: CHART_IMAGE_OPTIONS.y,
      },
    ];

    return {
      slideMaster: props.slideMaster,
      texts: [
        contentSlideHeaderV2(`Audience Profile (${index + 1}/${slideCount})`),
        ...annotationsTextContent(
          props.campaignAnalyticsReport.audienceProfile.takeaways.map(
            takeaway => takeaway.content
          ),
          props.campaignAnalyticsReport.audienceProfile.recommendations?.map(
            recommendation => recommendation.content
          )
        ),
      ],
      images,
    };
  });
};
